import { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";

const AnnotationDetailsPanel = ({
  isOpen,
  onClose,
  annotationIndex,
  annotation,
  onSaveData,
}) => {
  const panelRef = useRef(null);
  const overlayRef = useRef(null);
  const [comments, setComments] = useState(annotation?.comments || []);
  const [images, setImages] = useState(annotation?.images || []);
  const [newComment, setNewComment] = useState("");
  const [newImageUrl, setNewImageUrl] = useState("");

  useEffect(() => {
    if (annotation) {
      setComments(annotation.comments || []);
      let imagesList = annotation.images || [];

      // Si l'annotation a une image directe mais pas dans le tableau, l'ajouter
      if (annotation.image) {
        const imageExists = imagesList.some(
          (img) =>
            img.url === annotation.image ||
            (typeof img === "string" && img === annotation.image),
        );
        if (!imageExists) {
          imagesList = [
            {
              id: annotation.id || Date.now(),
              url: annotation.image,
              caption: "",
              date: annotation.date || new Date().toISOString(),
            },
            ...imagesList,
          ];
        }
      }

      // Normaliser les images (au cas où certaines sont des strings)
      imagesList = imagesList.map((img) => {
        if (typeof img === "string") {
          return {
            id: Date.now() + Math.random(),
            url: img,
            caption: "",
            date: annotation.date || new Date().toISOString(),
          };
        }
        return img;
      });

      setImages(imagesList);
    } else {
      setImages([]);
      setComments([]);
    }
  }, [annotation]);

  useEffect(() => {
    if (isOpen) {
      gsap.fromTo(
        panelRef.current,
        { x: 500, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.4, ease: "power3.out" },
      );
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.3 },
      );
    } else {
      gsap.to(panelRef.current, {
        x: 500,
        opacity: 0,
        duration: 0.3,
        ease: "power3.in",
      });
      gsap.to(overlayRef.current, {
        opacity: 0,
        duration: 0.2,
      });
    }
  }, [isOpen]);

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    const comment = {
      id: Date.now(),
      text: newComment,
      author: "Utilisateur",
      date: new Date().toISOString(),
    };
    const updated = [...comments, comment];
    setComments(updated);
    onSaveData({ ...annotation, comments: updated, images });
    setNewComment("");
  };

  const handleAddImage = () => {
    if (!newImageUrl.trim()) return;
    const image = {
      id: Date.now(),
      url: newImageUrl,
      caption: "",
      date: new Date().toISOString(),
    };
    const updated = [...images, image];
    setImages(updated);
    onSaveData({ ...annotation, comments, images: updated });
    setNewImageUrl("");
  };

  const handleDeleteComment = (id) => {
    const updated = comments.filter((c) => c.id !== id);
    setComments(updated);
    onSaveData({ ...annotation, comments: updated, images });
  };

  const handleDeleteImage = (id) => {
    const updated = images.filter((img) => img.id !== id);
    setImages(updated);
    onSaveData({ ...annotation, comments, images: updated });
  };

  if (!isOpen) return null;

  const getTypeLabel = (type) => {
    const labels = {
      USER_TRI: "Tri - Usager",
      USER_TRAVAUX: "Travaux - Usager",
      AGENT_ANOMALIE: "Anomalie - Agent",
      AGENT_AMELIORATION: "Amélioration - Agent",
    };
    return labels[type] || type;
  };

  return (
    <>
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black bg-opacity-30 z-40"
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col"
        style={{ transform: "translateX(500px)" }}
      >
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-[var(--akanthas-red)] to-[#c4152e] text-white">
          <div>
            <h2 className="text-2xl font-bold">
              Signalement {annotationIndex !== null ? annotationIndex + 1 : ""}
            </h2>
            <p className="text-sm opacity-90 mt-1">
              {annotation?.type ? getTypeLabel(annotation.type) : "Annotation"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors p-2 rounded-full hover:bg-white hover:bg-opacity-20"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="space-y-4">
            {annotation?.position?.description && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Position
                </h3>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-800">
                    {annotation.position.description}
                  </p>
                </div>
              </div>
            )}

            {(annotation?.description || annotation?.text) && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  Description
                </h3>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-800">
                    {annotation.description || annotation.text}
                  </p>
                </div>
              </div>
            )}

            {annotation?.closed && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <i className="fas fa-check-circle text-[var(--akanthas-red)]"></i>
                  <p className="text-sm font-medium text-[var(--akanthas-red)]">
                    Signalement clôturé
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Commentaires ({comments.length})
            </h3>
            <div className="space-y-3 mb-4">
              {comments.map((comment) => (
                <div
                  key={comment.id}
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        {comment.author}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(comment.date).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                  <p className="text-sm text-gray-700">{comment.text}</p>
                </div>
              ))}
              {comments.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  Aucun commentaire pour le moment
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Ajouter un commentaire..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--akanthas-red)]"
                onKeyPress={(e) => e.key === "Enter" && handleAddComment()}
              />
              <button
                onClick={handleAddComment}
                className="px-4 py-2 bg-[var(--akanthas-red)] text-white rounded-lg hover:bg-[#c4152e] transition-colors"
              >
                <i className="fas fa-plus"></i>
              </button>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Images ({images.length})
            </h3>
            <div className="grid grid-cols-2 gap-3 mb-4">
              {images.length > 0 ? (
                images.map((image) => (
                  <div key={image.id} className="relative group">
                    <img
                      src={image.url}
                      alt={image.caption || "Image"}
                      className="w-full h-32 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/200?text=Image+Error";
                      }}
                    />
                    <button
                      onClick={() => handleDeleteImage(image.id)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-sm text-gray-500 text-center py-4 col-span-2">
                  Aucune image pour le moment
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newImageUrl}
                onChange={(e) => setNewImageUrl(e.target.value)}
                placeholder="URL de l'image..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--akanthas-red)]"
                onKeyPress={(e) => e.key === "Enter" && handleAddImage()}
              />
              <button
                onClick={handleAddImage}
                className="px-4 py-2 bg-[var(--akanthas-red)] text-white rounded-lg hover:bg-[#c4152e] transition-colors"
              >
                <i className="fas fa-image"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AnnotationDetailsPanel;
